import { alpha } from "@mui/material/styles";
import type React from "react";
import { type FC, useMemo } from "react";
import { INK, INK_BLACK, NEUTRAL } from "../../../../theme/tokens";

export type InHouseBarStackedLoadingProps = {
	width?: React.CSSProperties["width"];
	height?: React.CSSProperties["height"];
	/** Number of bars to draw */
	bars?: number;
	/** Stacks per bar (fixed to 2 by default) */
	stacksPerBar?: 1 | 2;
	/** Show pill-style legend skeleton */
	showLegendSkeleton?: boolean;
	/** Visual toggles */
	showAxes?: boolean;
	showGrid?: boolean;
	/** Look & feel */
	cornerRadius?: number;
	background?: string;
	axisColor?: string;
	gridColor?: string;
	stackColors?: [string, string]; // [base, top]
	shimmer?: boolean;
	seed?: number; // deterministic heights
	margin?: {
		top: number;
		right: number;
		left: number;
		bottom: number;
	};
};

const DEFAULTS = {
	width: "100%",
	height: "100%",
	bars: 12,
	cornerRadius: 12,
	background: "transparent",
	axisColor: alpha(INK.SECONDARY, 0.5),
	gridColor: alpha(NEUTRAL[600], 0.15),
	stackColors: [alpha(INK.SECONDARY, 0.28), alpha(INK.SECONDARY, 0.48)] as [string, string],
};

function rngFactory(seed = 1337) {
	let s = seed >>> 0;
	return () => {
		s ^= s << 13;
		s ^= s >>> 17;
		s ^= s << 5;
		return (s >>> 0) / 0xffffffff;
	};
}

export const InHouseBarChartSkeleton: FC<InHouseBarStackedLoadingProps> = ({
	width = DEFAULTS.width,
	height = DEFAULTS.height,
	bars = DEFAULTS.bars,
	stacksPerBar = 2,
	showLegendSkeleton = false,
	showAxes = true,
	showGrid = true,
	cornerRadius = DEFAULTS.cornerRadius,
	background = DEFAULTS.background,
	axisColor = DEFAULTS.axisColor,
	gridColor = DEFAULTS.gridColor,
	stackColors = DEFAULTS.stackColors,
	shimmer = true,
	seed = 1337,
	margin,
}) => {
	const VB_W = 1000;
	const VB_H = 360;

	// Plot padding
	const padL = 40,
		padR = 20,
		padT = 16,
		padB = 36;
	const plotW = VB_W - padL - padR;
	const plotH = VB_H - padT - padB;

	const rng = useMemo(() => rngFactory(seed), [seed]);

	const barsGeom = useMemo(() => {
		const gapRatio = 0.6; // % of step reserved as gap
		const step = plotW / bars; // distance per category
		const barW = Math.max(6, step * (1 - gapRatio)); // visual bar width
		const gap = step - barW;

		// generate heights (base + top), keep within plot
		const items = Array.from({ length: bars }).map((_, i) => {
			const x = padL + i * step + gap / 2 + (step - gap - barW) / 2;
			const baseH = plotH * (0.25 + rng() * 0.55); // 25–80%
			const topH = stacksPerBar === 2 ? baseH * (0.2 + rng() * 0.35) : 0; // 20–55% of base
			// bottom anchored bars
			const baseY = padT + plotH - baseH;
			const topY = baseY - topH;
			return { x, barW, baseH, topH, baseY, topY };
		});
		return { items, step, barW, gap };
	}, [bars, plotW, plotH, rng, stacksPerBar]);

	// Grid lines (no border collision)
	const grid = useMemo(() => {
		const cols = 8,
			rows = 4;
		const v = Array.from({ length: cols - 1 }, (_, i) => padL + (plotW * (i + 1)) / cols);
		const h = Array.from({ length: rows - 1 }, (_, i) => padT + (plotH * (i + 1)) / rows);
		return { v, h };
	}, [plotW, plotH]);

	return (
		<div
			style={{
				width,
				height,
				background,
				borderRadius: cornerRadius,
				overflow: "hidden",
				display: "flex",
				flexDirection: "column",
				marginTop: margin?.top,
				marginRight: margin?.right,
				marginLeft: margin?.left,
				marginBottom: margin?.bottom,
			}}
			aria-label="Loading stacked bar chart skeleton"
			role="img"
		>
			<svg
				viewBox={`0 0 ${VB_W} ${VB_H}`}
				preserveAspectRatio="none"
				width="100%"
				height={showLegendSkeleton ? "calc(100% - 36px)" : "100%"}
				style={{ display: "block" }}
			>
				<defs>
					<clipPath id="ih-bar-clip">
						<rect x="8" y="8" width={VB_W - 16} height={VB_H - 16} rx="6" ry="6" />
					</clipPath>
				</defs>

				{/* Grid */}
				{showGrid && (
					<g stroke={gridColor} strokeDasharray="2 2" clipPath="url(#ih-bar-clip)">
						{grid.v.map((x) => (
							<line key={`gv-${x}`} x1={x} y1={padT} x2={x} y2={padT + plotH} />
						))}
						{grid.h.map((y) => (
							<line key={`gh-${y}`} x1={padL} y1={y} x2={padL + plotW} y2={y} />
						))}
					</g>
				)}

				{/* Axes */}
				{showAxes && (
					<g stroke={axisColor}>
						{/* left axis */}
						<line x1={padL} y1={padT} x2={padL} y2={padT + plotH} />
						{/* bottom axis */}
						<line x1={padL} y1={padT + plotH} x2={padL + plotW} y2={padT + plotH} />
					</g>
				)}

				{/* Bars */}
				<g clipPath="url(#ih-bar-clip)">
					{barsGeom.items.map((b, i) => (
						<g key={i}>
							{/* base stack */}
							<rect x={b.x} y={b.baseY} width={b.barW} height={b.baseH} fill={stackColors[0]} rx={2}>
								{shimmer && <animate attributeName="opacity" values="0.5;0.9;0.5" dur="2.8s" repeatCount="indefinite" />}
							</rect>
							{/* top stack (if enabled) */}
							{stacksPerBar === 2 && b.topH > 0 && (
								<rect x={b.x} y={b.topY} width={b.barW} height={b.topH} fill={stackColors[1]} rx={2}>
									{shimmer && <animate attributeName="opacity" values="0.5;0.9;0.5" dur="2.8s" begin="0.3s" repeatCount="indefinite" />}
								</rect>
							)}
						</g>
					))}
				</g>
			</svg>

			{showLegendSkeleton && (
				<div style={{ display: "flex", gap: 12, padding: "0px 12px 0 12px", flexWrap: "wrap" }}>
					{[0, 1].slice(0, stacksPerBar).map((i) => (
						<div
							key={`legend-${i}`}
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: 8,
								padding: "6px 10px",
								borderRadius: 999,
								background: alpha(INK_BLACK, 0.05),
							}}
						>
							<span
								style={{
									width: 10,
									height: 10,
									borderRadius: "50%",
									background: stackColors[i],
									opacity: 0.8,
								}}
							/>
							<span
								style={{
									width: 72,
									height: 10,
									borderRadius: 6,
									background: `linear-gradient(90deg, ${alpha(INK_BLACK, 0.08)} 25%, ${alpha(INK_BLACK, 0.14)} 37%, ${alpha(INK_BLACK, 0.08)} 63%)`,
									backgroundSize: "200% 100%",
									animation: shimmer ? "ih-skel 1.2s ease-in-out infinite" : "none",
								}}
							/>
						</div>
					))}
				</div>
			)}
			<style>
				{`
          @keyframes ih-skel {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}
			</style>
		</div>
	);
};
